import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Phone, Mail, Printer, FileDown, CalendarDays, ArrowRight } from "lucide-react";
import { api, docHref, fmtDate, fmtTime, track } from "../../lib/api";
import { useSite, useSeo, breadcrumb, faqSchema } from "../../lib/site";
import { ClassCard, FaqList, PageHero, CtaBlock, StaffCard, TestimonialCard, RichText } from "../../components/site/Cards";
import LeadForm from "../../components/site/LeadForm";
import { EmptyState, Spinner } from "../../components/common";

const useFetch = (url, init = null) => {
  const [d, setD] = useState(init);
  useEffect(() => { api.get(url).then((r) => setD(r.data)).catch(() => setD(Array.isArray(init) ? [] : {})); }, [url]); // eslint-disable-line
  return d;
};

export function Classes({ events = false }) {
  const data = useFetch(events ? "/public/events" : "/public/classes");
  const { programs } = useSite();
  const [prog, setProg] = useState("");
  useSeo({ title: events ? "Events & Class Schedule" : "Upcoming Healthcare Classes in Augusta, GA", description: "See upcoming CNA, Medical Assistant, Phlebotomy and EKG class start dates, schedules and seat availability at Healthcare Resources in Martinez, GA." });
  if (!data) return <Spinner />;
  const classes = (events ? data.classes : data) || [];
  const list = classes.filter((c) => !prog || c.program_id === prog);
  return (
    <>
      <PageHero eyebrow={events ? "Events" : "Class Schedule"} title={<>Upcoming <span className="text-red-600">{events ? "events & classes" : "classes"}</span></>}>Seat availability updates automatically as students enroll. Reserve your seat early — class sizes are small.</PageHero>
      <section className="container-x space-y-10 py-12">
        {events && (data.events || []).length > 0 && (
          <div className="grid gap-4 md:grid-cols-2" data-testid="events-list">
            {data.events.map((e) => (
              <div key={e.id} className="card p-5"><p className="eyebrow">{fmtDate(e.date)} {e.time && `· ${fmtTime(e.time)}`}</p><h3 className="mt-1 font-heading text-lg font-bold">{e.title}</h3><p className="mt-1 text-sm text-slate-600">{e.description}</p>{e.location && <p className="mt-2 flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5" />{e.location}</p>}</div>
            ))}
          </div>
        )}
        <div>
          <select aria-label="Filter by program" className="mb-6 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm" value={prog} onChange={(e) => setProg(e.target.value)} data-testid="classes-program-filter">
            <option value="">All programs</option>{programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div className="space-y-4" data-testid="classes-list">{list.map((c) => <ClassCard key={c.id} c={c} />)}</div>
          {!list.length && <EmptyState>No upcoming classes are currently available. Contact Admissions to discuss the next available start date.</EmptyState>}
        </div>
      </section>
      <CtaBlock />
    </>
  );
}

export function Admissions() {
  const docs = useFetch("/public/documents", []);
  useSeo({ title: "Admissions & Enrollment Requirements", description: "How to apply to Healthcare Resources in Martinez, GA: enrollment requirements, documents, steps and SpouseWorks application help.", schema: [breadcrumb([["Home", "/"], ["Admissions", "/admissions"]])] });
  const steps = [["Choose your program", "Compare programs, schedules and tuition — or use our program finder."], ["Create your account & apply", "Complete the online application in about 10 minutes."], ["Upload required documents", "Securely upload your ID, SS card, TB results and other program requirements."], ["Admissions review", "Our team reviews your application and contacts you about next steps."], ["Enroll & pay", "Choose a payment plan or funding source and reserve your seat."], ["Start learning", "Access your course materials and schedule in the student portal."]];
  return (
    <>
      <PageHero eyebrow="Admissions" title={<>How to <span className="text-red-600">apply & enroll</span></>}>A simple, guided process — with support from our admissions team at every step.</PageHero>
      <section className="container-x grid gap-12 py-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <ol className="space-y-5" data-testid="admissions-steps">
            {steps.map(([t, d], i) => (
              <li key={t} className="flex gap-4"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-slate-900 font-heading font-bold text-white">{i + 1}</span><div><h3 className="font-heading font-bold">{t}</h3><p className="text-sm text-slate-600">{d}</p></div></li>
            ))}
          </ol>
          <Link to="/apply" className="btn-primary mt-8" data-testid="admissions-apply-btn">Start Your Application <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="space-y-6 lg:col-span-5">
          <div className="card p-6"><h2 className="font-heading text-lg font-bold">To register you will need</h2><ul className="mt-3 space-y-2 text-sm text-slate-700"><li>• A valid government-issued picture ID</li><li>• Social Security card</li><li>• TB test results</li><li>• Criminal background check</li><li>• For some programs, high school diploma or GED</li></ul><p className="mt-3 text-xs text-slate-500">Each program has its own specific requirements — see the program page. High school seniors may enroll; certificates are awarded after graduation.</p></div>
          <div className="card p-6"><h2 className="font-heading text-lg font-bold">Downloads</h2><ul className="mt-3 space-y-2">{docs.map((d) => <li key={d.id}><a href={docHref(d)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-semibold text-red-600" data-testid={`public-doc-${d.id}`}><FileDown className="h-4 w-4" />{d.name}</a></li>)}{!docs.length && <li className="text-sm text-slate-500">No documents available.</li>}</ul></div>
          <div className="rounded-2xl bg-amber-50 p-5 text-sm text-amber-900"><strong>Payment security:</strong> we never ask you to email card numbers. Payments are made in person or through a secure link from Admissions.</div>
        </div>
      </section>
      <CtaBlock />
    </>
  );
}

export function Funding() {
  useSeo({ title: "Funding Options: Self Pay & SpouseWorks", description: "Pay for healthcare training in Augusta, GA with Self Pay (secure Square checkout and payment plans) or SpouseWorks for eligible programs." });
  return (
    <>
      <PageHero eyebrow="Funding" title={<>Funding & <span className="text-red-600">tuition assistance</span></>}>Two ways to fund your training: Self Pay or SpouseWorks. Funding approval is determined by the funding program and is not guaranteed.</PageHero>
      <section className="container-x grid gap-8 py-14 lg:grid-cols-2">
        <div className="card p-8" id="spouseworks" data-testid="funding-spouseworks">
          <p className="eyebrow">SpouseWorks</p>
          <h2 className="mt-2 font-heading text-2xl font-extrabold">We accept <span className="hl">SpouseWorks</span></h2>
          <p className="mt-4 text-sm text-slate-700">Select <strong>SpouseWorks</strong> as your funding source when you apply. Our admissions team will review your registration and guide you through the SpouseWorks process and any documents required. Funding approval is determined by SpouseWorks, not by Healthcare Resources.</p>
          <p className="mt-4 text-xs text-slate-500">Programs that accept SpouseWorks: Nursing Assistant, EKG Technician, Medical Assistant, Phlebotomy Technician.</p>
        </div>
        <div className="card p-8" id="self-pay" data-testid="funding-self-pay">
          <p className="eyebrow">Self Pay</p>
          <h2 className="mt-2 font-heading text-2xl font-extrabold">Secure Square checkout & payment plans</h2>
          <p className="mt-4 text-sm text-slate-700">Pay a deposit or full tuition through our secure Square checkout links on each program page. After paying, sign in to your student portal and <strong>submit your payment proof</strong> — our team verifies every payment against our Square records.</p>
          <p className="mt-2 text-sm text-slate-700">Most programs offer payment plans. Registration fees are separate from course fees.</p>
        </div>
      </section>
      <section className="container-x pb-14"><LeadForm form="Funding inquiry" title="Ask about funding eligibility" /></section>
    </>
  );
}

export function About() {
  const { site } = useSite();
  useSeo({ title: "About Healthcare Resources", description: "Healthcare Resources in Martinez, GA offers hands-on, career-focused healthcare training with over 30 years of experience." });
  return (
    <>
      <PageHero eyebrow="About" title={<>Opening doors to <span className="text-red-600">meaningful healthcare careers</span></>} />
      <section className="container-x grid gap-12 py-14 lg:grid-cols-2">
        <div><RichText text={site.about} /><div className="mt-6 flex gap-3"><Link to="/faculty" className="btn-dark" data-testid="about-faculty-btn">Meet our faculty</Link><Link to="/programs" className="btn-outline">Our programs</Link></div></div>
        <div className="card p-6"><h2 className="font-heading text-lg font-bold">Hybrid education & certification pathways</h2><p className="mt-3 text-sm text-slate-600">Our hybrid programs combine the convenience of online learning with the essential hands-on experience needed in healthcare training. Programs are designed to prepare students for certification through nationally recognized organizations, so you graduate trained, credentialed and ready for the workforce.</p><p className="mt-4 text-sm text-slate-600"><strong>Service area:</strong> {site.service_area}</p></div>
      </section>
      <CtaBlock />
    </>
  );
}

export function Faculty() {
  const staff = useFetch("/public/staff", null);
  useSeo({ title: "Faculty & Staff", description: "Meet the instructors and staff at Healthcare Resources in Martinez, GA." });
  return (
    <>
      <PageHero eyebrow="Faculty & Staff" title={<>Learn from <span className="text-red-600">experienced professionals</span></>} />
      <section className="container-x py-14">{!staff ? <Spinner /> : <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="staff-grid">{staff.map((s) => <StaffCard key={s.id} s={s} />)}</div>}</section>
    </>
  );
}

export function Success() {
  const t = useFetch("/public/testimonials", null);
  useSeo({ title: "Student Success Stories", description: "Hear from Healthcare Resources students and graduates." });
  return (
    <>
      <PageHero eyebrow="Student Success" title={<>Real students. <span className="text-red-600">Real careers.</span></>} />
      <section className="container-x py-14">{!t ? <Spinner /> : t.length ? <div className="grid gap-6 md:grid-cols-3">{t.map((x) => <TestimonialCard key={x.id} t={x} />)}</div> : <EmptyState>Student stories are coming soon. Are you a graduate? We'd love to hear from you.</EmptyState>}</section>
      <CtaBlock />
    </>
  );
}

export function Faq() {
  const faqs = useFetch("/public/faqs", null);
  useSeo({ title: "Frequently Asked Questions", description: "Answers about enrollment, requirements, funding, schedules and certification at Healthcare Resources.", schema: faqs?.length ? [faqSchema(faqs)] : [] });
  const cats = [...new Set((faqs || []).map((f) => f.category || "General"))];
  return (
    <>
      <PageHero eyebrow="FAQ" title={<>Questions, <span className="text-red-600">answered</span></>} />
      <section className="container-x max-w-4xl space-y-10 py-14">{!faqs ? <Spinner /> : cats.map((c) => <div key={c}><h2 className="mb-4 font-heading text-xl font-bold">{c}</h2><FaqList faqs={faqs.filter((f) => (f.category || "General") === c)} testid={`faq-cat-${c.toLowerCase()}`} /></div>)}</section>
      <CtaBlock />
    </>
  );
}

export function Blog() {
  const posts = useFetch("/public/blog", null);
  useSeo({ title: "Blog & Resources", description: "Healthcare career advice, certification tips and student resources from Healthcare Resources." });
  return (
    <>
      <PageHero eyebrow="Blog & Resources" title={<>Healthcare career <span className="text-red-600">insights</span></>} />
      <section className="container-x py-14">{!posts ? <Spinner /> : posts.length ? (
        <div className="grid gap-6 md:grid-cols-3">{posts.map((b) => (
          <Link key={b.id} to={`/blog/${b.slug}`} className="card overflow-hidden transition-shadow hover:shadow-lg" data-testid={`blog-card-${b.slug}`}>
            {b.featured_image && <img src={b.featured_image} alt="" loading="lazy" className="aspect-[16/9] w-full object-cover" />}
            <div className="p-5"><p className="eyebrow">{b.category}</p><h2 className="mt-2 font-heading text-lg font-bold">{b.title}</h2><p className="mt-2 text-sm text-slate-600">{b.excerpt}</p><p className="mt-3 text-xs text-slate-400">{fmtDate(b.publish_date)}</p></div>
          </Link>))}</div>
      ) : <EmptyState>No articles yet.</EmptyState>}</section>
    </>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/public/blog/${slug}`).then((r) => setB(r.data)).catch(() => setErr(true)); }, [slug]);
  useSeo(b ? { title: b.seo_title || b.title, description: b.seo_description || b.excerpt, image: b.featured_image, schema: [{ "@context": "https://schema.org", "@type": "BlogPosting", headline: b.title, datePublished: b.publish_date, author: { "@type": "Organization", name: b.author } }] } : {});
  if (err) return <div className="container-x py-20"><EmptyState>Article not found.</EmptyState></div>;
  if (!b) return <Spinner />;
  return (
    <article className="container-x max-w-3xl py-14">
      <p className="eyebrow">{b.category} · {fmtDate(b.publish_date)}</p>
      <h1 className="mt-3 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">{b.title}</h1>
      {b.featured_image && <img src={b.featured_image} alt="" className="my-8 aspect-[16/9] w-full rounded-2xl object-cover" />}
      <RichText text={b.content} />
      <CtaBlock />
    </article>
  );
}

export function Contact() {
  const { site } = useSite();
  useSeo({ title: "Contact Us — Martinez, GA", description: `Contact Healthcare Resources at ${site.address}, ${site.city}, GA. Call ${site.phone}.` });
  const addr = `${site.address}, ${site.city}, ${site.state} ${site.zip}`;
  return (
    <>
      <PageHero eyebrow="Contact" title={<>Visit or contact <span className="text-red-600">Admissions</span></>}>Serving {site.service_area}.</PageHero>
      <section className="container-x grid gap-10 py-14 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-5">
          <div className="card space-y-4 p-6" data-testid="contact-nap">
            <p className="flex gap-3 text-sm"><MapPin className="h-5 w-5 text-red-600" />{addr}</p>
            <p className="flex gap-3 text-sm"><Phone className="h-5 w-5 text-red-600" /><a href={`tel:${site.phone}`}>{site.phone}</a></p>
            <p className="flex gap-3 text-sm"><Printer className="h-5 w-5 text-red-600" />Fax: {site.fax}</p>
            <p className="flex gap-3 text-sm"><Mail className="h-5 w-5 text-red-600" /><a href={`mailto:${site.email}`}>{site.email}</a></p>
            <a className="btn-dark btn-sm" target="_blank" rel="noreferrer" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`} data-testid="contact-directions">Get directions</a>
          </div>
          {site.address && <iframe title="Map to Healthcare Resources" className="h-72 w-full rounded-2xl border-0" loading="lazy" src={`https://maps.google.com/maps?q=${encodeURIComponent(addr)}&output=embed`} />}
        </div>
        <div className="lg:col-span-7"><LeadForm form="Contact" title="Send us a message" /></div>
      </section>
    </>
  );
}

export function Legal({ kind }) {
  const { site } = useSite();
  useSeo({ title: kind === "privacy" ? "Privacy Policy" : "Terms of Use" });
  return (
    <>
      <PageHero title={kind === "privacy" ? "Privacy Policy" : "Terms of Use"} />
      <section className="container-x max-w-3xl py-12"><RichText text={(kind === "privacy" ? site.privacy_html : site.terms_html) || ""} /></section>
    </>
  );
}

export function NotFound() {
  useSeo({ title: "Page not found", noindex: true });
  return <div className="container-x py-24"><EmptyState>We couldn't find that page. <Link to="/" className="font-bold text-red-600">Go home</Link></EmptyState></div>;
}

export { CalendarDays, track };
